import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { api } from "../services/api";
import { applyThemeColors, removeThemeColors } from "../utils/color-utils";
import { useAuth } from "./AuthContext";

export interface TenantBranding {
  nome: string;
  slug: string;
  corPrimaria: string;
  corSecundaria: string;
  logoUrl?: string;
  faviconUrl?: string;
  /** Idioma do app mobile (pt-BR, pt-PT, en). Default pt-BR. */
  idioma?: string;
}

interface TenantThemeContextType {
  branding: TenantBranding | null;
  isLoading: boolean;
  updateTheme: (data: Partial<TenantBranding>) => Promise<void>;
  refreshBranding: () => Promise<void>;
}

const TenantThemeContext = createContext<TenantThemeContextType | undefined>(undefined);

const BRANDING_KEY = "payflow_branding";

export function useTenantTheme() {
  const context = useContext(TenantThemeContext);
  if (!context) {
    throw new Error("useTenantTheme must be used within a TenantThemeProvider");
  }
  return context;
}

export function TenantThemeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [branding, setBranding] = useState<TenantBranding | null>(() => {
    const cached = localStorage.getItem(BRANDING_KEY);
    return cached ? JSON.parse(cached) : null;
  });
  const [isLoading, setIsLoading] = useState(false);

  // Apply colors whenever branding changes
  useEffect(() => {
    if (branding?.corPrimaria) {
      applyThemeColors(branding.corPrimaria, branding.corSecundaria);
    } else {
      removeThemeColors();
    }
  }, [branding?.corPrimaria, branding?.corSecundaria]);

  // Apply favicon
  useEffect(() => {
    if (branding?.faviconUrl) {
      const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      if (link) {
        link.href = branding.faviconUrl;
      }
    }
  }, [branding?.faviconUrl]);

  // Fetch branding on auth change
  const refreshBranding = useCallback(async () => {
    if (!isAuthenticated) {
      setBranding(null);
      localStorage.removeItem(BRANDING_KEY);
      removeThemeColors();
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.get<TenantBranding>("/settings");
      if (response.data) {
        setBranding(response.data);
        localStorage.setItem(BRANDING_KEY, JSON.stringify(response.data));
      }
    } catch {
      // Use cached branding on error
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshBranding();
  }, [refreshBranding]);

  const updateTheme = useCallback(async (data: Partial<TenantBranding>) => {
    const response = await api.put<TenantBranding>("/settings/theme", data);
    if (response.data) {
      setBranding(response.data);
      localStorage.setItem(BRANDING_KEY, JSON.stringify(response.data));
    }
  }, []);

  return (
    <TenantThemeContext.Provider value={{ branding, isLoading, updateTheme, refreshBranding }}>
      {children}
    </TenantThemeContext.Provider>
  );
}
